// services/collectionsService.js
import api from './api';

class CollectionsService {
  /**
   * Get collections data
   * @param {Object} params - Query parameters (page, limit, filters)
   * @returns {Promise} API response
   */
  static async getCollections(params = {}) {
    try {
      // Use the correct endpoint from your API response
      const response = await api.get('/credits/collections', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching collections:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get collection details by ID
   * @param {number} id - Collection ID
   * @returns {Promise} API response
   */
  static async getCollectionById(id) {
    try {
      const response = await api.get(`/collections/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching collection details:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Create a new collection
   * @param {Object} collectionData - Collection data
   * @returns {Promise} API response
   */
  static async createCollection(collectionData) {
    try {
      const response = await api.post('/collections', collectionData);
      return response.data;
    } catch (error) {
      console.error('Error creating collection:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Update collection status
   * @param {number} id - Collection ID
   * @param {string} status - New status
   * @returns {Promise} API response
   */
  static async updateStatus(id, status) {
    try {
      const response = await api.put(`/collections/${id}/status`, { status });
      return response.data;
    } catch (error) {
      console.error('Error updating collection status:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Process collection payment
   * @param {Object} paymentData - Payment data
   * @returns {Promise} API response
   */
  static async processPayment(paymentData) {
    try {
      const response = await api.post('/collections/payment', paymentData);
      return response.data;
    } catch (error) {
      console.error('Error processing payment:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors
   * @param {Error} error - Error object
   * @returns {Object} Formatted error
   */
  static handleError(error) {
    if (error.response) {
      const { status, data } = error.response;
      
      switch (status) {
        case 400:
          return {
            success: false,
            message: data.message || 'Bad request. Please check your input.',
            errors: data.errors || [],
            status
          };
        
        case 401:
          return {
            success: false,
            message: 'Session expired. Please login again.',
            status
          };
        
        case 403:
          return {
            success: false,
            message: 'You do not have permission to perform this action.',
            status
          };
        
        case 404:
          return {
            success: false,
            message: 'Collections data not found.',
            status
          };
        
        case 422:
          return {
            success: false,
            message: 'Validation failed. Please check your input.',
            errors: data.errors || [],
            status
          };
        
        case 500:
          return {
            success: false,
            message: 'Server error. Please try again later.',
            status
          };
        
        default:
          return {
            success: false,
            message: data.message || 'An unexpected error occurred.',
            status
          };
      }
    } else if (error.request) {
      return {
        success: false,
        message: 'Network error. Please check your internet connection.',
        status: 0
      };
    } else {
      return {
        success: false,
        message: error.message || 'An unexpected error occurred.',
        status: -1
      };
    }
  }
}

export default CollectionsService;